import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Check, RotateCcw, CalendarDays, User, Building2, MessageCircle } from "lucide-react";
import { type Property, isActivePulseProperty } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { TodoCommentsThread, type MentionTeamMember } from "@/components/internal-comment-thread";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";

// Mirror of the server's TodoWithRefs read shape (server/storage.ts). The board
// is the only consumer, so a local type keeps it decoupled from server imports.
type TodoRow = {
  id: number;
  orgId: number;
  name: string;
  description: string | null;
  assigneeId: number;
  propertyId: number | null;
  dueDate: string | null;
  status: string; // "open" | "done"
  createdBy: number;
  createdAt: string;
  doneAt: string | null;
  doneBy: number | null;
  assigneeName: string | null;
  propertyName: string | null;
  createdByName: string | null;
  doneByName: string | null;
};

type TeamMemberRow = {
  id: number;
  userId: number;
  inviteeUserId: number | null;
  name: string | null;
  email: string;
  status: string;
};

type StatusFilter = "open" | "done" | "all";

// Local "today" in the host browser's timezone as YYYY-MM-DD, so a date-only
// due_date compares cleanly against the server's CURRENT_DATE overdue band.
function localTodayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function isOverdue(t: TodoRow): boolean {
  return t.status === "open" && t.dueDate != null && t.dueDate.slice(0, 10) < localTodayISO();
}

function formatDueDate(due: string): string {
  // due is a date-only "YYYY-MM-DD"; render without tz drift.
  const [y, m, d] = due.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return due;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AppOperationsTodos() {
  usePageTitle("To-Dos");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit("maintenance");
  const search = useSearch();

  // Deep-link: /app/operations/todos?todoId=N highlights (and reveals) one row.
  const deepLinkTodoId = useMemo(() => {
    const v = new URLSearchParams(search).get("todoId");
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  }, [search]);

  // A mention email/bell links to ?todoId=N&comment=M — when `comment` is also
  // present we open the comments dialog and highlight that comment. ?todoId
  // alone (assignment links) keeps the existing row-highlight-only behavior.
  const deepLinkCommentId = useMemo(() => {
    const v = new URLSearchParams(search).get("comment");
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  }, [search]);

  const meQuery = useQuery<{ user: { id: number } }>({ queryKey: ["/api/app/auth/me"] });
  const myUserId = meQuery.data?.user?.id ?? null;

  // When arriving via deep link, default to "all" so the target is visible
  // regardless of its status; otherwise default to the Open board.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(deepLinkTodoId != null ? "all" : "open");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [propertyFilter, setPropertyFilter] = useState<string>("all");

  const teamQuery = useQuery<TeamMemberRow[]>({ queryKey: ["/api/app/team-members"] });
  const assignees = useMemo(
    () =>
      (Array.isArray(teamQuery.data) ? teamQuery.data : [])
        .filter((m) => m.status === "accepted")
        .map((m) => ({ id: m.id === 0 ? m.userId : m.inviteeUserId, name: m.name || m.email || "Teammate" }))
        .filter((a): a is { id: number; name: string } => a.id != null),
    [teamQuery.data],
  );

  // @-mention picker source: `id` is the team-member ROW id (what the POST
  // `mentions` body + server fan-out match on) — a different domain than the
  // comment authorId (an app-user id), resolved separately below.
  const mentionMembers = useMemo<MentionTeamMember[]>(
    () =>
      (Array.isArray(teamQuery.data) ? teamQuery.data : [])
        .filter((m) => m.status === "accepted")
        .map((m) => ({ id: m.id, name: m.name, email: m.email })),
    [teamQuery.data],
  );
  // app-user id → display name, to label comment authors (authorId is an
  // app-user id; owner row is id 0 → userId, invitees → inviteeUserId).
  const authorNameByUserId = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of Array.isArray(teamQuery.data) ? teamQuery.data : []) {
      const uid = m.id === 0 ? m.userId : m.inviteeUserId;
      if (uid != null) map.set(uid, m.name || m.email || "Teammate");
    }
    return map;
  }, [teamQuery.data]);

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  // The to-do property picker deliberately lists EVERY property (active or not),
  // unlike the default active-only pickers — non-active ones are status-badged.
  const properties = Array.isArray(propertiesQuery.data) ? propertiesQuery.data : [];

  const todosQuery = useQuery<TodoRow[]>({
    queryKey: ["/api/app/todos", { status: statusFilter, assigneeId: assigneeFilter, propertyId: propertyFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ status: statusFilter });
      if (assigneeFilter !== "all") params.set("assigneeId", assigneeFilter);
      if (propertyFilter !== "all") params.set("propertyId", propertyFilter);
      return apiRequest("GET", `/api/app/todos?${params.toString()}`).then((r) => r.json());
    },
  });
  const todos = Array.isArray(todosQuery.data) ? todosQuery.data : [];

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ["/api/app/todos"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] });
  }

  // ── Create / edit dialog state ──────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [propertyId, setPropertyId] = useState<string>("none");
  const [dueDate, setDueDate] = useState<string>("");

  function openCreate() {
    setEditingId(null);
    setName("");
    setDescription("");
    setAssigneeId(myUserId != null ? String(myUserId) : "");
    setPropertyId("none");
    setDueDate("");
    setDialogOpen(true);
  }
  function openEdit(t: TodoRow) {
    setEditingId(t.id);
    setName(t.name);
    setDescription(t.description ?? "");
    setAssigneeId(String(t.assigneeId));
    setPropertyId(t.propertyId == null ? "none" : String(t.propertyId));
    setDueDate(t.dueDate ? t.dueDate.slice(0, 10) : "");
    setDialogOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        assigneeId: Number(assigneeId),
        propertyId: propertyId === "none" ? null : Number(propertyId),
        dueDate: dueDate || null,
      };
      return editingId == null
        ? apiRequest("POST", "/api/app/todos", body).then((r) => r.json())
        : apiRequest("PATCH", `/api/app/todos/${editingId}`, body).then((r) => r.json());
    },
    onSuccess: () => {
      invalidateAll();
      setDialogOpen(false);
      toast({ title: editingId == null ? "To-do created" : "To-do updated" });
    },
    onError: (err) =>
      toast({
        title: editingId == null ? "Couldn't create to-do" : "Couldn't update to-do",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      }),
  });

  const resolveMutation = useMutation({
    mutationFn: (t: TodoRow) => apiRequest("POST", `/api/app/todos/${t.id}/resolve`, {}).then((r) => r.json()),
    onSuccess: () => invalidateAll(),
    onError: (err) =>
      toast({ title: "Couldn't mark done", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });
  const reopenMutation = useMutation({
    mutationFn: (t: TodoRow) => apiRequest("POST", `/api/app/todos/${t.id}/reopen`, {}).then((r) => r.json()),
    onSuccess: () => invalidateAll(),
    onError: (err) =>
      toast({ title: "Couldn't reopen", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const [deleteTarget, setDeleteTarget] = useState<TodoRow | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (t: TodoRow) => apiRequest("DELETE", `/api/app/todos/${t.id}`, {}).then((r) => r.json()),
    onSuccess: () => {
      invalidateAll();
      setDeleteTarget(null);
      toast({ title: "To-do deleted" });
    },
    onError: (err) =>
      toast({ title: "Couldn't delete", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const canSave = name.trim().length > 0 && assigneeId !== "" && !Number.isNaN(Number(assigneeId));

  // Scroll the deep-linked row into view once it has rendered.
  const highlightRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (deepLinkTodoId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [deepLinkTodoId, todosQuery.data]);

  // ── Comments dialog ─────────────────────────────────────────────────────
  const [commentsTodo, setCommentsTodo] = useState<{ id: number; name: string } | null>(null);
  // Reported by the thread; gates dialog dismissal so an in-flight post/upload
  // can't be silently dropped by a stray click.
  const [commentBusy, setCommentBusy] = useState(false);

  // Auto-open the comments dialog (once) when the URL carries both ?todoId and
  // ?comment, i.e. the user followed a mention link.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (deepLinkTodoId == null || deepLinkCommentId == null) return;
    if (autoOpenedRef.current) return;
    const t = todos.find((x) => x.id === deepLinkTodoId);
    if (!t) return;
    autoOpenedRef.current = true;
    setCommentsTodo({ id: t.id, name: t.name });
  }, [deepLinkTodoId, deepLinkCommentId, todos]);

  // Following a mention link clears that bell row + unread count.
  useEffect(() => {
    if (deepLinkCommentId == null) return;
    apiRequest("PATCH", "/api/app/notifications/read-mention", {
      commentId: deepLinkCommentId,
      source: "todo_comment_mention",
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
      })
      .catch(() => {});
  }, [deepLinkCommentId, queryClient]);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="heading-todos" data-tour="todos-heading">To-Dos</h1>
            <p className="text-sm text-muted-foreground">
              Internal team to-dos. Each one shows up for its assignee under <span className="font-medium">Assigned to me</span> in the Team Inbox.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ExplainerVideoButton pageKey="todos" />
            <GuideButton pageKey="todos" />
            {!readOnly && (
              <Button onClick={openCreate} data-testid="button-add-todo" data-tour="todos-create">
                <Plus className="h-4 w-4 mr-1.5" /> New to-do
              </Button>
            )}
          </div>
        </div>

        {readOnly && <ViewOnlyBanner area="maintenance" />}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap" data-tour="todos-filters">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[140px]" data-testid="select-todo-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-todo-assignee-filter">
              <SelectValue placeholder="All assignees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              {myUserId != null && <SelectItem value={String(myUserId)}>Assigned to me</SelectItem>}
              {assignees
                .filter((a) => a.id !== myUserId)
                .map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <SearchableSelect
            value={propertyFilter}
            onValueChange={setPropertyFilter}
            placeholder="All properties"
            searchPlaceholder="Search properties..."
            emptyText="No properties match."
            testId="select-todo-property-filter"
            className="w-[200px]"
            options={[
              { value: "all", label: "All properties" },
              ...properties.map((p) => ({
                value: String(p.id),
                label: p.name,
                keywords: `${p.name ?? ""} ${p.address ?? ""}`,
              })),
            ]}
          />
        </div>

        {/* List */}
        {todosQuery.isLoading ? (
          <div className="space-y-2" data-tour="todos-board">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : todos.length === 0 ? (
          <Card data-tour="todos-board">
            <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="text-todos-empty">
              No to-dos here yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-tour="todos-board">
            {todos.map((t) => {
              const overdue = isOverdue(t);
              const done = t.status === "done";
              const isHighlighted = deepLinkTodoId === t.id;
              return (
                <Card
                  key={t.id}
                  ref={isHighlighted ? highlightRef : undefined}
                  className={`${overdue ? "border-red-300 bg-red-50/40" : ""} ${
                    isHighlighted ? "ring-2 ring-brand" : ""
                  }`}
                  data-testid={`card-todo-${t.id}`}
                >
                  <CardContent className="py-3 px-4 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`font-medium text-sm ${done ? "line-through text-muted-foreground" : "text-gray-900"}`}
                          data-testid={`text-todo-name-${t.id}`}
                        >
                          {t.name}
                        </span>
                        {overdue && (
                          <Badge variant="destructive" className="text-[10px] h-4 px-1.5" data-testid={`badge-todo-overdue-${t.id}`}>
                            Overdue
                          </Badge>
                        )}
                        {done && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5" data-testid={`badge-todo-done-${t.id}`}>
                            Done
                          </Badge>
                        )}
                      </div>
                      {t.description && (
                        <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap break-words">{t.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1" data-testid={`text-todo-assignee-${t.id}`}>
                          <User className="h-3 w-3" /> {t.assigneeName ?? "Unassigned"}
                        </span>
                        {t.dueDate && (
                          <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : ""}`} data-testid={`text-todo-due-${t.id}`}>
                            <CalendarDays className="h-3 w-3" /> {formatDueDate(t.dueDate)}
                          </span>
                        )}
                        {t.propertyName && (
                          <span className="inline-flex items-center gap-1" data-testid={`text-todo-property-${t.id}`}>
                            <Building2 className="h-3 w-3" /> {t.propertyName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCommentsTodo({ id: t.id, name: t.name })}
                        title="Comments"
                        data-testid={`button-todo-comments-${t.id}`}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      {!readOnly && (
                        <>
                          {done ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => reopenMutation.mutate(t)}
                              disabled={reopenMutation.isPending}
                              data-testid={`button-todo-reopen-${t.id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resolveMutation.mutate(t)}
                              disabled={resolveMutation.isPending}
                              data-testid={`button-todo-done-${t.id}`}
                            >
                              <Check className="h-3.5 w-3.5 mr-1" /> Mark done
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`button-todo-edit-${t.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!done && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => setDeleteTarget(t)}
                              data-testid={`button-todo-delete-${t.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / edit dialog. Gate dismissal while saving so a stray click
          can't drop an in-flight write. */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o && saveMutation.isPending) return;
          setDialogOpen(o);
        }}
      >
        <DialogContent
          onInteractOutside={(e) => saveMutation.isPending && e.preventDefault()}
          onEscapeKeyDown={(e) => saveMutation.isPending && e.preventDefault()}
          data-testid="dialog-todo-form"
        >
          <DialogHeader>
            <DialogTitle>{editingId == null ? "New to-do" : "Edit to-do"}</DialogTitle>
            <DialogDescription>
              Assign an internal task to a teammate. It will appear in their Team Inbox.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="todo-name">Name</Label>
              <Input
                id="todo-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What needs doing?"
                data-testid="input-todo-name"
              />
            </div>
            <div>
              <Label htmlFor="todo-description">Description</Label>
              <Textarea
                id="todo-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional details"
                rows={3}
                data-testid="input-todo-description"
              />
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger data-testid="select-todo-assignee">
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent>
                  {assignees.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.name}
                      {a.id === myUserId ? " (me)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="todo-due">Due date</Label>
                <Input
                  id="todo-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  data-testid="input-todo-due"
                />
              </div>
              <div>
                <Label>Property</Label>
                <SearchableSelect
                  value={propertyId}
                  onValueChange={setPropertyId}
                  placeholder="No property"
                  searchPlaceholder="Search properties..."
                  emptyText="No properties match."
                  testId="select-todo-property"
                  modal={true}
                  options={[
                    { value: "none", label: "No property" },
                    ...properties.map((p) => ({
                      value: String(p.id),
                      label: p.name,
                      keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                      labelNode: (
                        <span className="inline-flex items-center gap-2">
                          {p.name}
                          {!isActivePulseProperty(p) && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1">
                              {p.status}
                            </Badge>
                          )}
                        </span>
                      ),
                    })),
                  ]}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saveMutation.isPending} data-testid="button-todo-cancel">
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending} data-testid="button-todo-save">
              {saveMutation.isPending ? "Saving…" : editingId == null ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comments dialog. Dismissal is gated while a post/upload is in flight
          (the thread reports `commentBusy`) so a stray click can't drop it. */}
      <Dialog
        open={commentsTodo != null}
        onOpenChange={(o) => {
          if (!o && commentBusy) return;
          if (!o) setCommentsTodo(null);
        }}
      >
        <DialogContent
          className="max-w-lg"
          onInteractOutside={(e) => commentBusy && e.preventDefault()}
          onEscapeKeyDown={(e) => commentBusy && e.preventDefault()}
          onPointerDownOutside={(e) => commentBusy && e.preventDefault()}
          data-testid="dialog-todo-comments"
        >
          <DialogHeader>
            <DialogTitle className="truncate" data-testid="text-todo-comments-title">
              {commentsTodo?.name}
            </DialogTitle>
            <DialogDescription>Internal comments. @-mention a teammate to notify them.</DialogDescription>
          </DialogHeader>
          {commentsTodo && (
            <TodoCommentsThread
              todoId={commentsTodo.id}
              mentionMembers={mentionMembers}
              authorNameByUserId={authorNameByUserId}
              readOnly={readOnly}
              focusedCommentId={commentsTodo.id === deepLinkTodoId ? deepLinkCommentId : null}
              onBusyChange={setCommentBusy}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget != null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="dialog-todo-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this to-do?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-todo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget);
              }}
              data-testid="button-todo-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
